import { useEffect, useRef, useState, type CSSProperties } from "react";
import {
  Activity,
  Bell,
  Bot,
  Building2,
  ChevronDown,
  ClipboardCheck,
  ClipboardList,
  FileDown,
  FileText,
  GitBranch,
  Home,
  LayoutDashboard,
  KeyRound,
  LogOut,
  Megaphone,
  Menu,
  MessageSquare,
  PackageSearch,
  ReceiptText,
  Share2,
  Settings2,
  ShieldCheck,
  UserRound,
  UsersRound,
  X,
} from "lucide-react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import type { PortalConfig } from "../lib/types";

const publicNav = [
  { to: "/", label: "首页", end: true },
  { to: "/policies", label: "政策服务" },
  { to: "/products", label: "AI 产品" },
  { to: "/application", label: "申请入驻" },
  { to: "/community", label: "创业社区" },
];
export function Brand({ portal }: { portal?: PortalConfig }) {
  const shortName = portal?.branding.short_name || "OPC";
  return (
    <Link className="brand" to="/">
      <span className="brand-seal">{shortName.slice(0, 4)}</span>
      <span>
        <strong>{portal?.branding.name || "上诚 AI 创新港 OPC 社区"}</strong>
        <small>一人有志，众智成业</small>
      </span>
    </Link>
  );
}

export function PublicLayout() {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [portal, setPortal] = useState<PortalConfig>();
  const auth = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    api<PortalConfig>("/portal")
      .then(setPortal)
      .catch(() => undefined);
  }, []);
  useEffect(() => {
    if (auth.user)
      api<{ count?: number; unread_count?: number }>(
        "/notifications/unread-count",
      )
        .then((x) => setUnread(x.unread_count ?? x.count ?? 0))
        .catch(() => undefined);
  }, [auth.user]);
  return (
    <div
      className="public-shell"
      style={
        {
          "--blue": portal?.branding.primary_color,
          "--teal": portal?.branding.secondary_color,
        } as CSSProperties
      }
    >
      <header className="public-header">
        <Brand portal={portal} />
        <button
          className="icon-button mobile-only"
          aria-label="打开导航"
          onClick={() => setOpen(!open)}
        >
          {open ? <X /> : <Menu />}
        </button>
        <nav className={open ? "public-nav open" : "public-nav"}>
          {publicNav.map((item) => (
            <NavLink key={item.to} {...item} onClick={() => setOpen(false)}>
              {item.label}
            </NavLink>
          ))}
          {auth.user && (
            <>
              <NavLink className="mobile-only" to="/apps" onClick={() => setOpen(false)}>我的应用</NavLink>
              <NavLink className="mobile-only" to="/workspaces" onClick={() => setOpen(false)}>企业工作区</NavLink>
              <NavLink className="mobile-only" to="/wallet" onClick={() => setOpen(false)}>平台余额</NavLink>
              <NavLink className="mobile-only" to="/invite" onClick={() => setOpen(false)}>邀请奖励</NavLink>
            </>
          )}
        </nav>
        <div className="header-actions">
          {auth.user ? (
            <>
              <Link
                className="notification-link"
                to="/notifications"
                aria-label={`通知，${unread}条未读`}
              >
                <Bell size={19} />
                {unread > 0 && <i>{unread > 99 ? "99+" : unread}</i>}
              </Link>
              <div className="user-menu">
                <button>
                  <UserRound size={17} />
                  {auth.user.nickname || auth.user.masked_phone || "我的"}
                  <ChevronDown size={14} />
                </button>
                <div>
                  <Link to="/apps">我的应用</Link>
                  <Link to="/workspaces">企业工作区</Link>
                  <Link to="/wallet">平台余额</Link>
                  <Link to="/invite">邀请奖励</Link>
                  <Link to="/profile">个人资料</Link>
                  <Link to="/ventures">创业主体</Link>
                  {auth.hasRole("park_admin", "super_admin") && (
                    <Link to="/admin">园区管理</Link>
                  )}
                  <button
                    onClick={async () => {
                      await auth.logout();
                      navigate("/");
                    }}
                  >
                    <LogOut size={14} />
                    退出登录
                  </button>
                </div>
              </div>
            </>
          ) : (
            <Link className="secondary-button compact" to="/login">
              登录
            </Link>
          )}
          <Link className="primary-button compact" to="/application">
            申请入驻
          </Link>
        </div>
      </header>
      <main>
        <Outlet />
      </main>
      <footer className="public-footer">
        <div>
          <Brand portal={portal} />
          <p>
            {portal?.contents.find((item) => item.section === "footer")
              ?.subtitle ||
              portal?.branding.support_text ||
              "让政策触达、入驻办理和创业者协作更顺畅。"}
          </p>
        </div>
        <div className="footer-meta">
          <Link to="/legal/terms_of_service">服务协议</Link>
          <Link to="/legal/privacy_policy">隐私政策</Link>
          {portal?.contacts[0] && (
            <span>
              {portal.contacts[0].label} {portal.contacts[0].value}
            </span>
          )}
          {portal?.branding.icp_text && <span>{portal.branding.icp_text}</span>}
        </div>
      </footer>
    </div>
  );
}

const adminNav = [
  {
    to: "/admin",
    label: "运营总览",
    section: "overview",
    icon: LayoutDashboard,
    end: true,
    roles: ["park_admin", "super_admin"],
  },
  {
    to: "/admin/products",
    label: "产品与 Provider",
    section: "ai",
    icon: PackageSearch,
    roles: ["park_admin", "super_admin"],
  },
  {
    to: "/admin/product-apps",
    label: "企业应用运行",
    section: "ai",
    icon: Bot,
    roles: ["park_admin", "super_admin"],
  },
  {
    to: "/admin/billing",
    label: "余额与对账",
    section: "ai",
    icon: ReceiptText,
    roles: ["park_admin", "super_admin"],
  },
  {
    to: "/admin/aff-risk",
    label: "邀请与风控",
    section: "ai",
    icon: Share2,
    roles: ["park_admin", "super_admin"],
  },
  {
    to: "/admin/applications",
    label: "入驻审核",
    section: "park",
    icon: ClipboardCheck,
    roles: ["park_admin", "park_reviewer", "park_viewer", "super_admin"],
  },
  {
    to: "/admin/review-tasks",
    label: "审核任务",
    section: "park",
    icon: ClipboardList,
    roles: ["park_admin", "park_reviewer", "park_viewer", "super_admin"],
  },
  {
    to: "/admin/mcp-keys",
    label: "管理员 MCP",
    section: "system",
    icon: KeyRound,
    roles: ["park_reviewer", "super_admin"],
  },
  {
    to: "/admin/merchants",
    label: "商户管理",
    section: "park",
    icon: Building2,
    roles: ["park_admin", "park_viewer", "super_admin"],
  },
  {
    to: "/admin/programs",
    label: "项目与表单",
    section: "park",
    icon: GitBranch,
    roles: ["park_admin", "super_admin"],
  },
  {
    to: "/admin/policies",
    label: "政策管理",
    section: "park",
    icon: FileText,
    roles: ["park_admin", "super_admin", "policy_editor", "policy_approver"],
  },
  {
    to: "/admin/announcements",
    label: "公告管理",
    section: "park",
    icon: Megaphone,
    roles: ["park_admin", "community_moderator", "super_admin"],
  },
  {
    to: "/admin/community",
    label: "论坛管理",
    section: "park",
    icon: MessageSquare,
    roles: ["park_admin", "super_admin"],
  },
  {
    to: "/admin/portal",
    label: "园区门户",
    section: "park",
    icon: Settings2,
    roles: ["park_admin", "super_admin"],
  },
  {
    to: "/admin/users",
    label: "账号权限",
    section: "system",
    icon: UsersRound,
    roles: ["super_admin"],
  },
  {
    to: "/admin/audit",
    label: "审计日志",
    section: "system",
    icon: ShieldCheck,
    roles: ["super_admin"],
  },
  {
    to: "/admin/exports",
    label: "数据导出",
    section: "system",
    icon: FileDown,
    roles: ["super_admin"],
  },
  {
    to: "/admin/operations",
    label: "异步任务",
    section: "system",
    icon: Activity,
    roles: ["super_admin"],
  },
];
const adminNavSections = [
  { key: "overview", label: "" },
  { key: "ai", label: "AI 产品服务" },
  { key: "park", label: "园区服务" },
  { key: "system", label: "平台治理" },
] as const;

export function AdminLayout() {
  const [open, setOpen] = useState(false);
  const mainRef = useRef<HTMLDivElement>(null);
  const openButtonRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const auth = useAuth();
  const navigate = useNavigate();
  const visibleAdminNav = adminNav.filter((item) => auth.hasRole(...item.roles));
  useEffect(() => {
    const compact = window.matchMedia("(max-width: 1040px)");
    const closeAtDesktop = (event: MediaQueryListEvent) => {
      if (!event.matches) setOpen(false);
    };
    compact.addEventListener("change", closeAtDesktop);
    return () => compact.removeEventListener("change", closeAtDesktop);
  }, []);
  useEffect(() => {
    if (!open || !window.matchMedia("(max-width: 1040px)").matches) return;
    const previousOverflow = document.body.style.overflow;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.body.style.overflow = "hidden";
    mainRef.current?.setAttribute("inert", "");
    window.addEventListener("keydown", closeOnEscape);
    closeButtonRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      mainRef.current?.removeAttribute("inert");
      window.removeEventListener("keydown", closeOnEscape);
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [open]);
  return (
    <div className="admin-shell">
      {open && <button className="admin-sidebar-backdrop mobile-only" tabIndex={-1} aria-label="关闭管理导航" onClick={() => setOpen(false)} />}
      <aside id="admin-sidebar" className={open ? "admin-sidebar open" : "admin-sidebar"} role={open ? "dialog" : undefined} aria-modal={open || undefined} aria-label="园区管理导航">
        <div className="admin-brand">
          <span className="brand-seal">OPC</span>
          <span>
            <strong>园区运营中心</strong>
            <small>管理工作台</small>
          </span>
          <button
            ref={closeButtonRef}
            className="icon-button mobile-only"
            aria-label="关闭管理导航"
            aria-controls="admin-sidebar"
            onClick={() => setOpen(false)}
          >
            <X />
          </button>
        </div>
        <nav aria-label="管理导航">
          {adminNavSections.map((section) => {
            const items = visibleAdminNav.filter((item) => item.section === section.key);
            if (!items.length) return null;
            return <section className="admin-nav-group" aria-label={section.label || "总览"} key={section.key}>
              {section.label && <span className="admin-nav-label">{section.label}</span>}
              <div>
                {items.map((item) => {
                  const Icon = item.icon;
                  return <NavLink key={item.to} to={item.to} end={item.end} onClick={() => setOpen(false)}>
                    <Icon size={17} />
                    <span>{item.label}</span>
                  </NavLink>;
                })}
              </div>
            </section>;
          })}
        </nav>
        <Link className="back-link" to="/">
          <Home size={16} />
          返回创业者门户
        </Link>
      </aside>
      <div ref={mainRef} className="admin-main" aria-hidden={open || undefined}>
        <header className="admin-topbar">
          <button
            ref={openButtonRef}
            className="icon-button mobile-only"
            aria-label="打开管理导航"
            aria-controls="admin-sidebar"
            aria-expanded={open}
            onClick={() => setOpen(true)}
          >
            <Menu />
          </button>
          <div>
            <span>OPC PARK OPERATIONS</span>
            <strong>园区运营工作台</strong>
          </div>
          <div className="admin-user">
            <span>{auth.user?.nickname || "管理员"}</span>
            <button
              className="icon-button"
              aria-label="退出"
              onClick={async () => {
                await auth.logout();
                navigate("/");
              }}
            >
              <LogOut size={18} />
            </button>
          </div>
        </header>
        <main className="admin-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
